import { Typography } from "@/components/shared/typography";
import { Image, View } from "@/components/ui";
import { useAuthState } from "@/store/auth";
import { PackageDepartmentEnum } from "@/__generated__/graphql";
import { useBookingState } from "@/store/booking/useBookingState";
import { HorizontalScroll } from "@/components/shared/horizontal-scroll";
import { Card, CardContent } from "@/components/ui/card";
import { WorkerAvatar } from "@/components/shared/worker-avatar";
import type { Worker } from "@/types/booking";
import { CheckCircle, Circle, Star } from "@/components/shared/icons";
import { useClientPastWorkersQuery } from "@/gqlhooks/profile/useClientPastWorkersQuery";
import { cn, getPlatform } from "@/lib/utils";
import { mapPastWorkerDetail } from "@/gqlhooks/profile/useClientPastWorkersQuery";
import { timeRanges } from "@/lib/booking-lib";
import { formatDate } from "@/lib/helpers/date";
import Employee from "@/assets/images/3d-icons/employee.png";
import { addDays } from "date-fns";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";

export const PastMassageTherapistWorkerSection = () => {
  const clientId = useAuthState((state) => state.data.userInfo.id);
  const setBookingState = useBookingState((state) => state.setBookingState);
  const selectedWorkers = useBookingState(
    (state) => state.data.selectedWorkers,
  );
  const postalCode = useBookingState((state) =>
    clientId ? state.data.selectedAddress?.postalCode : state.data.postalCode,
  );
  const startDate = useBookingState((state) => state.data.startDate);
  const endDate = useBookingState((state) => state.data.endDate);
  const selectedPackageIds = useBookingState(
    (state) => state.data.selectedPackageIds,
  );
  const { workers, loading } = useClientPastWorkersQuery({
    skip: !clientId,
    variables: {
      filters: {
        clientId,
        department: PackageDepartmentEnum.Massage,
        limit: 3,
        ensureTimeslotAvailability: true,
        timeRanges,
        postalCode: postalCode,
        startDate: formatDate(startDate),
        endDate: formatDate(addDays(endDate, 14)),
        lineItemIds: selectedPackageIds,
      },
    },
    onCompleted: ({ clientFindPastWorkersV2: { workers } }) => {
      const [firstWorker] = workers;
      if (firstWorker) {
        setBookingState({
          selectedWorkers: [mapPastWorkerDetail(firstWorker)],
        });
      }
    },
  });

  const cardWidth = useWindowDimensions().width / 2.5;

  const handleSelectAllTherapist = () => {
    setBookingState({
      selectedWorkers: [],
    });
  };

  const handleSelectWorker = (worker: Worker) => {
    setBookingState({
      selectedWorkers: [worker],
    });
  };

  if (loading || workers.length === 0) {
    return null;
  }

  return (
    <View className="flex w-full flex-col gap-3">
      <Typography variant="label-sm">Recently booked therapist</Typography>
      <AllTherapistButton
        checked={selectedWorkers.length === 0}
        onClick={handleSelectAllTherapist}
      />
      <HorizontalScroll
        data={workers}
        snap="start"
        itemSpacing={8}
        itemWidth={getPlatform() === "web" ? 230 : cardWidth}
        renderItem={(worker) => {
          return (
            <MassageWorkerCard
              key={worker.id}
              selected={selectedWorkers.some(
                (selectedWorker) => selectedWorker.id === worker.id,
              )}
              worker={worker}
              onClick={() => handleSelectWorker(worker)}
            />
          );
        }}
      />
    </View>
  );
};

const AllTherapistButton = ({
  checked,
  onClick,
}: {
  checked: boolean;
  onClick: () => void;
}) => {
  return (
    <Card onClick={onClick}>
      <CardContent className="flex flex-row items-center justify-between gap-2 p-2">
        <View className="flex flex-row items-center gap-2">
          <Image src={Employee} width={22} height={22} />
          <Typography variant="label-sm">All therapists</Typography>
        </View>
        {checked ? (
          <CheckCircle weight="fill" size={22} className="text-primary" />
        ) : (
          <Circle size={22} className="text-border-medium" />
        )}
      </CardContent>
    </Card>
  );
};

const MassageWorkerCard = ({
  worker,
  onClick,
  selected,
}: {
  worker: Worker;
  selected: boolean;
  onClick: () => void;
}) => {
  return (
    <Card
      className={cn(
        "my-1 h-[120px] w-full overflow-hidden border-2",
        selected && "border-primary-border",
      )}
      onClick={onClick}
    >
      <CardContent className="flex flex-col gap-3">
        <WorkerAvatar src={worker.avatarUrl} />
        <Typography variant="label-sm" numberOfLines={1}>
          {worker.fullName}
        </Typography>
        <View className="flex flex-row items-center gap-1">
          <Star weight="fill" className="text-warning" size={12} />
          <Typography variant="caption-md" numberOfLines={1}>
            {Number(worker.workerRating).toFixed(1)}/5 •
          </Typography>
          <Typography variant="caption-md" numberOfLines={1}>
            {worker.recentCompletedVisitsInMonth} recent sessions
          </Typography>
        </View>
      </CardContent>
    </Card>
  );
};
